import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

import { ButtonPlacement } from '../../../../components/CopyField';
import RadioButtonGroup from '../../RadioButtonGroup';
import { getOnboardingLinkFromContract } from '../../../utils/onboardingLink';
import { ContractType } from '../../../utils/userContract';
import ContractModalContext from '../context/ContractModalContext';
import { ContractState } from '../utils/ContractTypes';
import ContractConfigurator from '../ContractConfigurator';

import { Section } from '../Section';
import { Toolbar } from '../ContractConfigurator/styles';
import {
  DetailsPanel,
  StyledContractDetails,
  StyledCopyField,
  Title,
  InfoBox,
} from './styles';
import texts from './texts.json';
import { ContractEditorTabs, contractEditorTabsConfig } from './tabs';

const ContractEditor = ({
  showFeesConfigurator,
  showChallengeProducts,
  showPostCommitmentConfiguration,
}) => {
  const {
    coachDoc,
    externalCoachDoc,
    contract,
    contractState,
    selectedProduct,
    isReadOnly,
    contractTerms,
    loadContractFromDoc,
    onContractFieldChange,
    onPackageSelected,
    onProductChange,
    contractDoc,
    secondaryContractDoc,
    lead,
    isReonboardingContract,
    isAmendment,
    isPreviewTerms,
    contractType,
    onboardingLink,
    canSaveChallengeForLead,
    isSelectedChallengeSaved,
  } = useContext(ContractModalContext);

  /*
   * A challenge's link names the challenge's own contract, which only sells to this lead once the
   * lead carries it — so it is worth copying after the rep saves the challenge, not before. Every
   * other offer is resolved from the lead alone and has nothing to wait for.
   */
  const canCopyOnboardingLink = !canSaveChallengeForLead || isSelectedChallengeSaved;

  const [selectedTab, setSelectedTab] = useState(ContractEditorTabs.CONTRACT);

  const hasSecondaryContract = !!secondaryContractDoc;
  const discountPercentage = secondaryContractDoc?.price?.initialDiscountPercentage;

  const contractOptionsConfig = useMemo(() => {
    const options = contractEditorTabsConfig(discountPercentage);
    return [ContractEditorTabs.CONTRACT, ContractEditorTabs.SECONDARY_CONTRACT].map((tab) => ({
      label: options[tab].label,
      value: tab,
    }));
  }, [discountPercentage]);

  // Set the appropriate contract data in the context when the tab is changed.
  const onTabChange = useCallback((tab) => {
    if (tab === ContractEditorTabs.SECONDARY_CONTRACT) {
      loadContractFromDoc(secondaryContractDoc);
    } else {
      loadContractFromDoc(contractDoc);
    }
    setSelectedTab(tab);
  }, [loadContractFromDoc, secondaryContractDoc, contractDoc]);

  return (
    <>
      {contractState === ContractState.CONFIGURATION && !isReadOnly && (
        <ContractConfigurator
          selectedProduct={selectedProduct}
          onProductChange={onProductChange}
          contract={contract}
          contractDoc={contractDoc}
          onChange={onContractFieldChange}
          isReadOnly={isReadOnly}
          onPackageChange={onPackageSelected}
          supportedCurrencies={externalCoachDoc?.supportedCurrencies}
          showFeesConfigurator={showFeesConfigurator}
          showChallengeProducts={showChallengeProducts}
          showPostCommitmentConfiguration={showPostCommitmentConfiguration}
          isAmendment={isAmendment}
        />
      )}
      {(contractState === ContractState.DETAILS || isReadOnly) && (
        <DetailsPanel>
          {(hasSecondaryContract && !isPreviewTerms) ? (
            <>
              <Toolbar>
                <RadioButtonGroup
                  options={contractOptionsConfig}
                  selectedOption={selectedTab}
                  onOptionChange={(option) => {
                    if (option !== selectedTab) {
                      onTabChange(option);
                    }
                  }}
                />
              </Toolbar>
              <Section>
                <Title>{texts.detailsTitle}</Title>
                <StyledContractDetails
                  contractTerms={contractTerms}
                  coachConditions={contract.coachConditions}
                  isAmendment={isAmendment}
                />
                <InfoBox>
                  <InfoIcon />
                  {texts.upfrontPlanInfo}
                </InfoBox>
              </Section>
            </>
          ) : (
            <Section>
              <Title>{texts.detailsTitle}</Title>
              <StyledContractDetails
                contractTerms={contractTerms}
                coachConditions={contract.coachConditions}
                isAmendment={isAmendment}
              />
            </Section>
          )}
          {!!lead?.id && !isReadOnly && !isReonboardingContract && !isPreviewTerms && canCopyOnboardingLink && (
            <Section>
              <StyledCopyField
                id="copy-contract-link"
                label={texts.copyLink}
                value={onboardingLink}
                valuePrefix={format(texts.linkPrefix, { leadName: lead?.name, coachName: coachDoc?.name })}
                placement={ButtonPlacement.START}
              />
            </Section>
          )}
          {contractType === ContractType.ONE_TIME && !!contractDoc && (
            <Section>
              <StyledCopyField
                id="copy-contract-link"
                label={texts.copyLink}
                value={getOnboardingLinkFromContract(contractDoc.id)}
                placement={ButtonPlacement.START}
              />
            </Section>
          )}
        </DetailsPanel>
      )}
    </>
  );
};

ContractEditor.propTypes = {
  showFeesConfigurator: PropTypes.bool,
  showChallengeProducts: PropTypes.bool,
  showPostCommitmentConfiguration: PropTypes.bool,
};

ContractEditor.defaultProps = {
  showFeesConfigurator: false,
  showChallengeProducts: false,
  showPostCommitmentConfiguration: false,
};

export default compose(
  observer,
)(ContractEditor);
